import { createTheme } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';

export const colors = {
  primary: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
  },
  secondary: {
    50: '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    300: '#c4b5fd',
    400: '#a78bfa',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
    800: '#5b21b6',
    900: '#4c1d95',
  },
  success: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.025em',
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.02em',
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  button: {
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.01em',
  },
} as const;

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
} as const;

const getPalette = (mode: PaletteMode) => {
  const isLight = mode === 'light';
  return {
    mode,
    primary: {
      main: colors.primary[500],
      light: colors.primary[400],
      dark: colors.primary[700],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary[500],
      light: colors.secondary[400],
      dark: colors.secondary[700],
      contrastText: '#ffffff',
    },
    success: {
      main: colors.success[500],
      light: colors.success[400],
      dark: colors.success[700],
    },
    warning: {
      main: colors.warning[500],
      light: colors.warning[400],
      dark: colors.warning[700],
    },
    error: {
      main: colors.error[500],
      light: colors.error[400],
      dark: colors.error[700],
    },
    background: {
      default: isLight ? '#f0f2f5' : '#0c0f1a',
      paper: isLight ? '#ffffff' : '#161b2e',
    },
    text: {
      primary: isLight ? colors.grey[900] : '#e8eaf0',
      secondary: isLight ? colors.grey[500] : colors.grey[400],
    },
    divider: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)',
  };
};

const getComponents = (mode: PaletteMode): Components<Omit<Theme, 'components'>> => {
  const isLight = mode === 'light';
  return {
    MuiButton: {
      styleOverrides: {
        root: () => ({
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none' as const,
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (hover: hover)': {
            '&:hover': {
              boxShadow: isLight
                ? '0 4px 14px rgba(99, 102, 241, 0.35)'
                : '0 4px 14px rgba(99, 102, 241, 0.25)',
              transform: 'translateY(-1px)',
            },
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }),
        contained: () => ({
          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
          },
        }),
        outlined: () => ({
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: isLight
              ? 'rgba(99, 102, 241, 0.04)'
              : 'rgba(99, 102, 241, 0.08)',
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: () => ({
          borderRadius: 16,
          boxShadow: isLight
            ? '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)'
            : '0 2px 8px rgba(0, 0, 0, 0.3)',
          border: isLight
            ? '1px solid rgba(0, 0, 0, 0.04)'
            : '1px solid rgba(255, 255, 255, 0.06)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (hover: hover)': {
            '&:hover': {
              boxShadow: isLight
                ? '0 20px 40px rgba(0, 0, 0, 0.08), 0 8px 16px rgba(0, 0, 0, 0.04)'
                : '0 20px 40px rgba(0, 0, 0, 0.4)',
              transform: 'translateY(-2px)',
            },
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: () => ({
          borderRadius: 16,
          boxShadow: isLight
            ? '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)'
            : '0 2px 8px rgba(0, 0, 0, 0.25)',
          backgroundImage: 'none',
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: () => ({
          boxShadow: isLight
            ? '0 1px 0 rgba(0, 0, 0, 0.05)'
            : '0 1px 0 rgba(255, 255, 255, 0.05)',
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: () => ({
          borderRight: 'none',
          boxShadow: isLight
            ? '4px 0 24px rgba(0, 0, 0, 0.06)'
            : '4px 0 24px rgba(0, 0, 0, 0.3)',
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: () => ({
          borderRadius: 12,
          margin: '2px 12px',
          padding: '10px 16px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-selected': {
            backgroundColor: isLight ? colors.primary[500] : 'rgba(99, 102, 241, 0.2)',
            color: isLight ? '#ffffff' : colors.primary[200],
            '&:hover': {
              backgroundColor: isLight ? colors.primary[600] : 'rgba(99, 102, 241, 0.25)',
            },
            '& .MuiListItemIcon-root': {
              color: isLight ? '#ffffff' : colors.primary[200],
            },
            '& .MuiListItemText-primary': {
              color: isLight ? '#ffffff' : colors.primary[100],
              fontWeight: 600,
            },
          },
          '&:hover': {
            backgroundColor: isLight ? colors.grey[100] : 'rgba(255, 255, 255, 0.04)',
          },
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: () => ({
          '& .MuiTableCell-head': {
            backgroundColor: isLight ? '#f8f9fb' : 'rgba(255, 255, 255, 0.03)',
            fontWeight: 600,
            fontSize: '0.8rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase' as const,
            color: isLight ? colors.grey[500] : colors.grey[400],
            borderBottom: `1px solid ${isLight ? colors.grey[200] : 'rgba(255, 255, 255, 0.06)'}`,
            padding: '14px 16px',
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: () => ({
          borderBottom: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)'}`,
          padding: '14px 16px',
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: () => ({
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: isLight
              ? 'rgba(99, 102, 241, 0.03)'
              : 'rgba(99, 102, 241, 0.04)',
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: () => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary[400],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px',
              borderColor: colors.primary[500],
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.15)'}`,
            },
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: () => ({
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.01em',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: () => ({
          borderRadius: 20,
          boxShadow: isLight
            ? '0 25px 50px rgba(0, 0, 0, 0.12)'
            : '0 25px 50px rgba(0, 0, 0, 0.5)',
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: () => ({
          textTransform: 'none' as const,
          fontWeight: 500,
          fontSize: '0.9rem',
          minHeight: 44,
          '&.Mui-selected': {
            fontWeight: 600,
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: () => ({
          height: 3,
          borderRadius: '3px 3px 0 0',
          background: 'linear-gradient(90deg, #6366F1, #8B5CF6)',
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: () => ({
          borderRadius: 8,
          fontSize: '0.8rem',
          padding: '6px 12px',
          backgroundColor: isLight ? colors.grey[800] : colors.grey[700],
        }),
      },
    },
  };
};

export const createAppTheme = (mode: PaletteMode = 'light') =>
  createTheme({
    palette: getPalette(mode),
    typography,
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    breakpoints,
    components: getComponents(mode),
  });
